// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import { injectable } from 'inversify';
import Axios from 'axios';

import StaffUser, { StaffUserDTO } from '@shared/models/staff-user';
import { ListRequestParams } from '@shared/types/services';
import BaseService from '@core/services/base';
import { USER_FIELDS } from '@shared/constants/user';
import { ActivityFilter, REQUEST_PARAMS, SortingType } from '@shared/constants/services';

@injectable()
export default class StaffService extends BaseService<StaffUserDTO, StaffUser> {
  static diToken = Symbol('staff-service');

  private defaultPageSize = 1000;

  constructor() {
    super({ domainArea: 'users', Model: StaffUser });
  }

  getListQuery_org({ pagination, sorting, filtering }: ListRequestParams) {
    return `
      {
        users(
          ${REQUEST_PARAMS.pagination.fields.page}: ${pagination.page}, 
          ${REQUEST_PARAMS.pagination.fields.pageSize}: ${pagination.pageSize}, 
          sort: { field: ${sorting?.field}, type: ${sorting?.type} },
          ${REQUEST_PARAMS.filtering.fields.searchKeyword}: "${filtering?.searchKeyword || ''}",
          ${REQUEST_PARAMS.filtering.fields.activityFilter}: ${filtering?.activityFilter ?? ActivityFilter.all}
        ) {
          items {
            ${USER_FIELDS.title},
            ${USER_FIELDS.email},
            ${USER_FIELDS.profileStatus},
            ${USER_FIELDS.profilePhoto},
            ${USER_FIELDS.roles},
            ${USER_FIELDS.lastActivityDate},
            ${USER_FIELDS.fullName},
            ${USER_FIELDS.id},
            ${USER_FIELDS.isBlocked},
            ${USER_FIELDS.twoFactorEnabled},
            ${USER_FIELDS.isActive},
            ${USER_FIELDS.companyId},
            ${USER_FIELDS.companyName}
          }
          pageInfo {
            totalCount,
            hasPreviousPage,
            hasNextPage
          }
        }
      }
    `;
  }

  // Build the params for the GraphQL query to get list of users.
  getListQuery({ pagination, sorting, filtering }: ListRequestParams) {
    // valid fields for sorting: fullName, title, and lastActivityDate.
    return `
      {
        users(
          ${REQUEST_PARAMS.pagination.fields.page}: ${pagination?.page || 0}, 
          ${REQUEST_PARAMS.pagination.fields.pageSize}: ${pagination?.pageSize || this.defaultPageSize}, 
          sort: { field: ${sorting?.field || "fullName"}, type: ${sorting?.type || SortingType.asc} }, 
          ${REQUEST_PARAMS.filtering.fields.searchKeyword}: "${filtering?.searchKeyword || ''}",
          ${REQUEST_PARAMS.filtering.fields.activityFilter}: ${filtering?.activityFilter ?? ActivityFilter.all}
        ) {
          items {
            ${USER_FIELDS.title},
            ${USER_FIELDS.email},
            ${USER_FIELDS.profileStatus},
            ${USER_FIELDS.profilePhoto},
            ${USER_FIELDS.roles},
            ${USER_FIELDS.lastActivityDate},
            ${USER_FIELDS.fullName},
            ${USER_FIELDS.id},
            ${USER_FIELDS.isBlocked},
            ${USER_FIELDS.twoFactorEnabled},
            ${USER_FIELDS.isActive},
            ${USER_FIELDS.companyId},
            ${USER_FIELDS.companyName}
          }
          pageInfo {
            totalCount,
            hasPreviousPage,
            hasNextPage
          }
        }
      }
    `;
  }

  async validateActivationLink(userId: StaffUser['id'], activationToken: string) {
    try {
      const url = `${this.urlPrefix}/${userId}/activate`;
      const { data } = await Axios.post(
        url,
        {},
        { params: { activationToken } }
      );

      return data.token;
    } catch (err) {
      throw err;
    }
  }

  activateAccount(userId: StaffUser['id'], data: { resetPasswordToken: string; newPassword: string; }) {
    const url = `${this.urlPrefix}/${userId}/password`;

    return Axios.put(url, data);
  }

  resetPassword(email: string) {
    const url = `${this.urlPrefix}/password/change/request/send`;

    return Axios.post(url, { email });
  }

  validateRecoveryLink(userId: StaffUser['id'], token: string) {
    const url = `${this.urlPrefix}/${userId}/password/token/validate?passwordRecoveryToken=${token}`;

    return Axios.put(url);
  }

  setNewPassword(userId: StaffUser['id'], data: { newPassword: string; confirmPassword: string; token: string; }) {
    const url = `${this.urlPrefix}/${userId}/password/reset`;

    return Axios.put(url, data);
  }

  resendInvitation(userId: StaffUser['id']) {
    const url = `${this.urlPrefix}/${userId}/invitation/resend`;

    return Axios.post(url);
  }
}
